import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { FaTrash, FaPlus, FaComments, FaPencilAlt } from 'react-icons/fa';
import AdSlot from './AdSlot';

const AFFILIATE_URL = process.env.REACT_APP_AFFILIATE_URL;

const SidebarContainer = styled.div`
  width: 250px;
  background-color: ${theme.colors.backgroundLight};
  border-right: 1px solid ${theme.colors.border};
  display: flex;
  flex-direction: column;
  height: 100%;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 300px; /* Adjust for mobile if needed */
    display: none; /* Hide on mobile for now or use a toggle */
  }
`;

const SidebarHeader = styled.div`
  padding: ${theme.spacing.md};
  border-bottom: 1px solid ${theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  color: ${theme.colors.text};
  font-size: 1rem;
`;

const NewChatButton = styled.button`
  background: ${theme.gradients.primary};
  border: none;
  border-radius: ${theme.borderRadius.small};
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ChatList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.sm};
`;

const ChatItem = styled.div<{ isActive: boolean }>`
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.medium};
  background-color: ${props => props.isActive ? theme.colors.background : 'transparent'};
  border: 1px solid ${props => props.isActive ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.background};
  }
`;

const ChatInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  overflow: hidden;
`;

const ChatName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.9rem;
`;

const DateText = styled.span`
    display: block;
    font-size: 0.7rem;
    color: ${theme.colors.textSecondary};
    margin-top: 2px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  padding: 4px;

  &:hover {
    color: ${theme.colors.text};
  }
`;

const DeleteButton = styled(IconButton)`
  &:hover {
    color: ${theme.colors.error};
  }
`;

const ChatActions = styled.div`
  display: flex;
  gap: 2px;
`;

const RenameInput = styled.input`
  flex: 1;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.small};
  color: ${theme.colors.text};
  font-size: 0.9rem;
  padding: 4px 6px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
`;

const EmptyState = styled.div`
    padding: ${theme.spacing.lg};
    text-align: center;
    color: ${theme.colors.textSecondary};
    font-size: 0.9rem;
`;

const SidebarFooter = styled.div`
    border-top: 1px solid ${theme.colors.border};
    padding: ${theme.spacing.sm};
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const AffiliateCTA = styled.a`
    display: block;
    width: 100%;
    text-align: center;
    padding: ${theme.spacing.sm};
    background: ${theme.gradients.primary};
    color: white;
    border-radius: ${theme.borderRadius.medium};
    text-decoration: none;
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: ${theme.spacing.sm};

    &:hover {
        opacity: 0.9;
    }
`;

interface Chat {
  id: string;
  title: string;
  updatedAt?: any;
}

interface ChatSidebarProps {
  chats: Chat[];
  activeChatId: string | null;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  onDeleteChat: (chatId: string) => void;
  onRenameChat: (chatId: string, title: string) => Promise<void> | void;
  loading: boolean;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  chats,
  activeChatId,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  onRenameChat,
  loading
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  const startEditing = (chat: Chat) => {
    setEditingId(chat.id);
    setDraftTitle(chat.title || 'New Chat');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setDraftTitle('');
  };

  const commitEditing = async () => {
    if (!editingId) return;
    const title = draftTitle.trim();
    const original = chats.find(c => c.id === editingId)?.title || '';
    setEditingId(null);
    if (!title || title === original) return;
    try {
      await onRenameChat(editingId, title);
    } catch (err) {
      console.error('Rename failed', err);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    // Timestamp object check for Firestore or serialized date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    // Determine if date is valid
    if (isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  };

  return (
    <SidebarContainer>
      <SidebarHeader>
        <Title>Your Chats</Title>
        <NewChatButton
          onClick={onNewChat}
          disabled={loading}
          title="Start new chat"
        >
          {/* @ts-ignore */}
          <FaPlus /> New
        </NewChatButton>
      </SidebarHeader>

      <ChatList>
        {chats.length === 0 ? (
          <EmptyState>No saved chats yet.</EmptyState>
        ) : (
          chats.map(chat => (
            <ChatItem
              key={chat.id}
              isActive={chat.id === activeChatId}
              onClick={() => editingId !== chat.id && onSelectChat(chat.id)}
            >
              <ChatInfo>
                {/* @ts-ignore */}
                <FaComments color={theme.colors.secondary} />
                <div style={{ overflow: 'hidden', flex: 1 }}>
                  {editingId === chat.id ? (
                    <RenameInput
                      ref={inputRef}
                      value={draftTitle}
                      onChange={e => setDraftTitle(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onBlur={commitEditing}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); commitEditing(); }
                        else if (e.key === 'Escape') { e.preventDefault(); cancelEditing(); }
                      }}
                      maxLength={100}
                    />
                  ) : (
                    <>
                      <ChatName title={chat.title}>{chat.title || 'Untitled Chat'}</ChatName>
                      <DateText>{formatDate(chat.updatedAt)}</DateText>
                    </>
                  )}
                </div>
              </ChatInfo>
              {editingId !== chat.id && (
                <ChatActions>
                  <IconButton
                    title="Rename"
                    onClick={(e) => {
                      e.stopPropagation();
                      startEditing(chat);
                    }}
                  >
                    {/* @ts-ignore */}
                    <FaPencilAlt size={11} />
                  </IconButton>
                  <DeleteButton
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this chat?')) {
                        onDeleteChat(chat.id);
                      }
                    }}
                  >
                    {/* @ts-ignore */}
                    <FaTrash size={12} />
                  </DeleteButton>
                </ChatActions>
              )}
            </ChatItem>
          ))
        )}
      </ChatList>

      <SidebarFooter>
        {AFFILIATE_URL && (
          <AffiliateCTA href={AFFILIATE_URL} target="_blank" rel="noopener noreferrer">
            Talk to real people →
          </AffiliateCTA>
        )}
        <AdSlot id="sidebar-bottom" width={300} height={250} />
      </SidebarFooter>
    </SidebarContainer>
  );
};

export default ChatSidebar;
